import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle,
  faChartLine,
  faCalendarAlt,
  faExclamationTriangle,
  faClock,
  faUserGraduate,
  faBalanceScaleLeft
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';

const StatCard = styled.div`
  background: ${props => props.theme.cardBackground};
  border-left: 4px solid ${props => props.color || '#88167a'};
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  transition: transform 0.3s;

  &:hover {
    transform: translateY(-3px);
  }

  .stat-title {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 5px;
  }

  .stat-value {
    font-size: 1.4rem;
    font-weight: 600;
    color: #343a40;
  }
`;

const ProgressBarContainer = styled.div`
  height: 10px;
  background: #e9ecef;
  border-radius: 5px;
  margin: 10px 0;
  overflow: hidden;

  .progress-bar {
    height: 100%;
    background: ${props => props.color || '#88167a'};
    border-radius: 5px;
    transition: width 0.5s ease;
  }
`;

const DayCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 15px;
  height: 100%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  .day-name {
    font-weight: 600;
    color: #88167a;
    font-size: 1.1rem;
    margin-bottom: 5px;
  }
  
  .day-hours {
    font-size: 0.9rem;
    color: #6c757d;
    margin-bottom: 10px;
  }
  
  .time-slots {
    border-top: 1px solid #f0f0f0;
    padding-top: 8px;
  }
  
  .time-slot {
    font-size: 0.85rem;
    
    .time-value {
      font-weight: 500;
      color: #495057;
    }
    
    .program-count {
      color: #6c757d;
      background: #f8f9fa;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 0.8rem;
    }
  }
`;

const AlertItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f1f1;

  .alert-icon {
    color: #F44336;
    margin-right: 10px;
  }
`;



const TeacherAnalyticsModal = ({ teacher, onClose }) => {
    const [isOpen, setIsOpen] = useState(false);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
    useEffect(() => {
      setIsOpen(!!teacher);
    }, [teacher]);

    // Helper function to format time (convert "14" to "2:00 PM")
const formatTime = (timeString) => {
    const time = parseInt(timeString);
    const hours = time % 12 || 12;
    const ampm = time < 12 || time === 24 ? 'AM' : 'PM';
    return `${hours}:00 ${ampm}`;
  };
  
    const handleClose = () => {
      setIsOpen(false);
      if (onClose) onClose();
    };
  
    if (!teacher) return null;
  
    // Calculate utilization metrics
    const weeklyUtilization = Math.min(100, (teacher.usedWeekHr / teacher.weekHr) * 100);
    const dailyLimits = teacher.dayHr || 8;
    const hasViolations = teacher.excess;
  
    // Generate daily breakdown data
    const dailyBreakdown = {};
    if (teacher.assignedPrograms) {
      teacher.assignedPrograms.forEach(program => {
        const day = program.day;
        dailyBreakdown[day] = (dailyBreakdown[day] || 0) + 1;
      });
    }
  
    // Generate violations data
    const violations = {
      weeklyExcess: teacher.excess ? Math.abs(teacher.availableWeekHr) : 0,
      dailyExcess: []
    };
  
    for (const day in dailyBreakdown) {
      if (dailyBreakdown[day] > dailyLimits) {
        violations.dailyExcess.push({
          day: parseInt(day),
          hours: dailyBreakdown[day],
          limit: dailyLimits
        });
      }
    }
  
    return (
      <Modal 
        isOpen={isOpen} 
        toggle={handleClose} 
        size="lg"
        centered
        className="teacher-analytics-modal"
      >
        <ModalHeader 
          toggle={handleClose}
          className="bg-primary text-white"
        >
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faUserCircle} className="me-3" />
            <div>
              <h4 className="mb-0" style={{color:"white"}}>{teacher.name}'s Analytics</h4>
              <small className="text-white-50">ID: {teacher.id}</small>
            </div>
          </div>
        </ModalHeader>
        
        <ModalBody>
          <div className="row">
            {/* Basic Info Column */}
            <div className="col-md-4">
              <StatCard color="#6A3A8F">
                <div className="stat-title">
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  Timezone
                </div>
                <div className="stat-value">
                  {teacher.timezone || 'Not specified'}
                </div>
              </StatCard>
  
              <StatCard color="#00D1B2">
                <div className="stat-title">
                  <FontAwesomeIcon icon={faUserGraduate} className="me-2" />
                  Assigned Children
                </div>
                <div className="stat-value">
                  {teacher.assignedCount || 0}
                </div>
              </StatCard>
  
              <StatCard color="#88167a">
                <div className="stat-title">
                  <FontAwesomeIcon icon={faBalanceScaleLeft} className="me-2" />
                  Weekly Capacity
                </div>
                <div className="stat-value">
                  {teacher.usedWeekHr || 0}/{teacher.weekHr || 0} hrs
                </div>
                <ProgressBarContainer 
                  color={teacher.excess ? '#F44336' : '#88167a'}
                >
                  <div 
                    className="progress-bar" 
                    style={{ width: `${weeklyUtilization}%` }}
                  />
                </ProgressBarContainer>
              </StatCard>
            </div>
  
            {/* Main Content Column */}
            <div className="col-md-8">
  <div className="mb-4">
    <h5>
      <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
      Assignment Overview
    </h5>
    <div className="row">
      {days.map((dayName, dayIndex) => {
        // Filter programs for this specific day
        const dayPrograms = teacher.assignedPrograms?.filter(
          program => parseInt(program.day) === dayIndex
        ) || [];

        // Group programs by time
        const timeGroups = {};
        dayPrograms.forEach(program => {
          const time = program.time || '0'; // Handle potential undefined time
          if (!timeGroups[time]) {
            timeGroups[time] = [];
          }
          timeGroups[time].push(program);
        });

        // Sort time slots chronologically
        const sortedTimes = Object.keys(timeGroups).sort((a, b) => parseInt(a) - parseInt(b));

        return (
          <div key={dayIndex} className="col-12 col-md-6 col-lg-4 mb-3">
            <DayCard>
              <div className="day-name">{dayName}</div>
              <div className="day-hours">
                {dayPrograms.length} {dayPrograms.length === 1 ? 'program' : 'programs'}
              </div>
              
              {dayPrograms.length > 0 ? (
                <div className="time-slots mt-2">
                  {sortedTimes.map(time => (
                    <div key={time} className="time-slot mb-1">
                      <div className="d-flex justify-content-between">
                        <span className="time-value">
                          {formatTime(time)}
                        </span>
                        <span className="program-count">
                          {timeGroups[time].length} {timeGroups[time].length === 1 ? 'child' : 'children'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted small mt-2">No assignments</div>
              )}

              <ProgressBarContainer 
                color={dayPrograms.length > dailyLimits ? '#F44336' : '#00D1B2'}
              >
                <div 
                  className="progress-bar" 
                  style={{ width: `${Math.min(100, (dayPrograms.length / dailyLimits) * 100)}%` }}
                />
              </ProgressBarContainer>
            </DayCard>
          </div>
        );
      })}
    </div>
  </div>

              {hasViolations && (
                <div className="alert alert-danger">
                  <h5>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Capacity Alerts
                  </h5>
                  {violations.weeklyExcess > 0 && (
                    <AlertItem>
                      <span className="alert-icon">⚠️</span>
                      <span>
                        Weekly overload: {violations.weeklyExcess} hours over limit
                      </span>
                    </AlertItem>
                  )}
                  
                  {violations.dailyExcess.length > 0 && (
                    <>
                      <h6 className="mt-3">Daily Overloads</h6>
                      {violations.dailyExcess.map((violation, i) => (
                        <AlertItem key={i}>
                          <span className="alert-icon">⚠️</span>
                          <span>
                            {days[violation.day]}: {violation.hours} hours (limit: {violation.limit})
                          </span>
                        </AlertItem>
                      ))}
                    </>
                  )}
                </div>
              )}
  
              <div className="mt-4">
                <h5>
                  <FontAwesomeIcon icon={faUserGraduate} className="me-2 text-primary" />
                  Qualification Levels
                </h5>
                <div className="d-flex flex-wrap gap-2">
                  {teacher.levels?.map((levelId, index) => (
                    <span key={index} className="badge bg-primary">
                      Level {levelId}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    );
  };
  
  export default TeacherAnalyticsModal;